import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { useCookies } from 'react-cookie'
import { Skeleton } from 'primereact/skeleton'

import BizinBackend from '../../helpers/bizin_helper'
import LogoDefault from '../../components/Icons/LogoDefault'
import FlagBR from '../../assets/images/Bandeira-Brasil.svg'
import FlagUS from '../../assets/images/Bandeira-Eua.svg'

const translations = {
  'pt-BR': {
    backButton: 'Voltar',
    contentNotFound: 'Conteúdo não encontrado.',
    errorLoadingContent: 'Erro ao carregar o conteúdo.',
    copyright:
      '© {year} Copyright Grupo e-Makers - Todos os direitos reservados'
  },
  'en-US': {
    backButton: 'Back',
    contentNotFound: 'Content not found.',
    errorLoadingContent: 'Error loading content.',
    copyright: '© {year} Copyright Grupo e-Makers - All rights reserved'
  }
}

const LegalContentPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const params = new URLSearchParams(location.search)

  let tipo = params.get('tipo')
  if (!tipo) {
    if (location.pathname === '/BI_EX0004') {
      tipo = 'politica'
    } else {
      tipo = 'termos'
    }
  }

  const [cookies, setCookie] = useCookies(['_csrf_token'])
  const [loading, setLoading] = useState(true)
  const [htmlContent, setHtmlContent] = useState('')
  const [language, setLanguage] = useState('pt-BR')
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const dropdownRef = useRef(null)

  const currentTranslations = translations[language] || translations['pt-BR']

  const handleBack = () => navigate(-1)

  const handleToggleDropdown = () => {
    setDropdownOpen(prev => !prev)
  }

  const handleSelectLanguage = lang => {
    setLanguage(lang)
    setDropdownOpen(false)
  }

  const getCsrf = useCallback(async () => {
    try {
      const _csrf = await BizinBackend.getCsrfToken()
      if (_csrf?.csrfToken) {
        setCookie('_csrf_token', _csrf, { path: '/' })
        return _csrf.csrfToken
      } else {
        throw new Error('Resposta inválida ao obter CSRF token')
      }
    } catch (error) {
      return null
    }
  }, [setCookie])

  useEffect(() => {
    const fetchContent = async () => {
      setLoading(true)
      try {
        let token = cookies?._csrf_token?.csrfToken

        if (!token) {
          console.warn('CSRF token não encontrado no cookie. Buscando...')
          token = await getCsrf()
          if (!token) throw new Error('Falha ao obter CSRF token')
        }

        const response = await BizinBackend.executeProcedure(
          {
            1: language,
            2: tipo
          },
          'FRONT_BI_EX0003',
          token
        )

        setHtmlContent(
          response?.[0]?.HTML_Termos ||
            `<p>${currentTranslations.contentNotFound}</p>`
        )
      } catch (err) {
        console.error('Erro ao carregar o conteúdo:', err)
        setHtmlContent(`<p>${currentTranslations.errorLoadingContent}</p>`)
      } finally {
        setLoading(false)
      }
    }

    fetchContent()
  }, [cookies, language, tipo, getCsrf, currentTranslations])

  useEffect(() => {
    const handleClickOutside = e => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div className="terms-container">
      <div className="terms-header">
        <div className="terms-side left">
          <button onClick={handleBack} className="terms-back-button">
            <i className="fas fa-arrow-left"></i>{' '}
            {currentTranslations.backButton}
          </button>
        </div>

        <div className="terms-logo">
          <Link to="/login">
            <LogoDefault width="186.82" height="50" color="#ffffff" />
          </Link>
        </div>

        <div className="terms-side right">
          <div
            className={`terms-language ${dropdownOpen ? 'dropdown-open' : ''}`}
            ref={dropdownRef}
          >
            <button onClick={handleToggleDropdown} className="lang-button">
              <img
                src={language === 'en-US' ? FlagUS : FlagBR}
                alt="Idioma"
                className="flag-icon"
              />
              <svg
                className="chevron-icon"
                width="14"
                height="8"
                viewBox="0 0 14 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12.292 0.292912C12.6825 -0.0976372 13.3166 -0.0976372 13.7071 0.292912C14.0976 0.68346 14.0976 1.31771 13.7071 1.70826L7.70914 7.70709C7.31864 8.09764 6.68448 8.09764 6.29399 7.70709L0.292871 1.71138C-0.0976236 1.32084 -0.0976236 0.686584 0.292871 0.296036C0.683365 -0.0945131 1.31753 -0.0945131 1.70802 0.296036L7 5.58563L12.292 0.292912Z"
                  fill="white"
                />
              </svg>
            </button>

            {dropdownOpen && (
              <div className="lang-dropdown">
                <div
                  className={`dropdown-item ${
                    language === 'en-US' ? 'selected-language-item' : ''
                  }`}
                  onClick={() => handleSelectLanguage('en-US')}
                >
                  <img src={FlagUS} alt="US" />
                </div>
                <div
                  className={`dropdown-item ${
                    language === 'pt-BR' ? 'selected-language-item' : ''
                  }`}
                  onClick={() => handleSelectLanguage('pt-BR')}
                >
                  <img src={FlagBR} alt="BR" />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="terms-content">
        {loading ? (
          <Skeleton width="100%" height="500px" />
        ) : (
          <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
        )}
      </div>

      <footer className="terms-footer">
        {}
        {currentTranslations.copyright.replace(
          '{year}',
          new Date().getFullYear()
        )}
      </footer>
    </div>
  )
}

export default LegalContentPage
